import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, isBefore, isEqual, getDay, format, startOfToday } from "date-fns";
import siteText from "../assets/siteText.json";
import { isFeatureEnabled } from "../utils/featureFlags";
import BookaletBookingPage from "../components/BookaletBookingPage";

function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    start: null, // now a Date object
    end: null,   // now a Date object
    guests: 1,
    pets: 0,
    message: ""
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  // Pricing state
  const [pricing, setPricing] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  // Fetch pricing data from server
  useEffect(() => {
    fetch('/api/pricing')
      .then(res => res.json())
      .then(data => {
        setPricing(data);
        setLoadingPricing(false);
      })
      .catch(err => {
        setPricingError('Could not load pricing data');
        setLoadingPricing(false);
      });
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // --- Pricing calculation logic ---
  // Match selected arrival date (Friday) to the 'Week Commencing' column in the CSV, use 'Weekly' column for price
  function getWeeklyPriceForDate(date) {
    if (!pricing || pricing.length === 0 || !date) return null;
    // Format the date to match the CSV's 'Week Commencing' format (e.g., '3-Jan-2025')
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    const formatted = date.toLocaleDateString('en-GB', options).replace(/\b([A-Za-z]+)\b/, (m) => m.charAt(0).toUpperCase() + m.slice(1,3).toLowerCase());
    // The CSV may use e.g. '3-Jan-2025', so let's try to match that
    for (const row of pricing) {
      if (row['Week Commencing']) {
        // Accept both with and without leading zeros
        const csvDate = row['Week Commencing'].replace(/^0/, '');
        if (
          csvDate === formatted ||
          new Date(csvDate).toDateString() === date.toDateString()
        ) {
          return parseFloat(row['Weekly']?.replace(/[^\d.]/g, ''));
        }
      }
    }
    // fallback: first price if no match
    return pricing[0]?.['Weekly'] ? parseFloat(pricing[0]['Weekly'].replace(/[^\d.]/g, '')) : null;
  }

  function calculateTotalPrice() {
    if (!form.start || !form.end) return null;
    const msPerDay = 24*60*60*1000;
    const nights = Math.round((form.end - form.start) / msPerDay);
    const weeks = nights / 7;
    if (weeks !== 1 && weeks !== 2) return null; // Only 7 or 14 night stays allowed
    const weeklyPrice = getWeeklyPriceForDate(form.start);
    if (!weeklyPrice) return null;
    let total = weeklyPrice * weeks;
    // £20 per dog per stay, as charged by Bookalet
    const petFee = form.pets > 0 ? form.pets * 20 : 0;
    total += petFee;
    return {
      weeklyPrice,
      weeks,
      petFee,
      total
    };
  }

  // Remove modal logic: not needed with disabled datepickers


  // Helper: only allow Fridays from today onwards
  function filterArrival(date) {
    const today = startOfToday();
    return getDay(date) === 5 && !isBefore(date, today);
  }
  // Helper: get the next Friday after a given date
  function getNextFriday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (5 + 7 - day) % 7 || 7; // days to next Friday
    return addDays(d, diff);
  }

  // Helper: for departure, only allow 7 or 14 nights after arrival (or after today if no arrival)
  function getValidDepartures() {
    const base = form.start ? form.start : getNextFriday(startOfToday());
    return [addDays(base, 7), addDays(base, 14)];
  }
  function filterDeparture(date) {
    const validDepartures = getValidDepartures();
    return validDepartures.some(d => isEqual(d, date));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    // Defensive: ensure valid dates
    if (!form.start || !form.end) return;
    if (parseInt(form.pets, 10) > 2) {
      setSubmitStatus("error");
      alert("You can book a maximum of 2 pets per booking.");
      return;
    }
    try {
      // Attach pricing breakdown
      const priceInfo = calculateTotalPrice();
      const response = await fetch("/api/booking-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          start: format(form.start, "yyyy-MM-dd"),
          end: format(form.end, "yyyy-MM-dd"),
          pets: form.pets,
          pricing: priceInfo ? {
            weeklyPrice: priceInfo.weeklyPrice,
            weeks: priceInfo.weeks,
            petFee: priceInfo.petFee,
            total: priceInfo.total
          } : null
        })
      });
      if (response.ok) {
        setSubmitStatus("success");
        setForm({ name: "", email: "", start: null, end: null, guests: 1, pets: 0, message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
    }
  };



  if (submitStatus === "success") {
    return (
      <div className="container">
        <h2>Thank you for your booking request!</h2>
        <p>We will get back to you shortly.</p>
      </div>
    );
  }

  return (
    <>

      <div className="container">
        {isFeatureEnabled('useBookalet') ? (
          <BookaletBookingPage />
        ) : (
          <>
            <h2>{siteText.booking.header}</h2>
            <p style={{ fontSize: "1.07rem", margin: "1.2rem 0 2.2rem 0" }}>
              {(() => {
                const parts = siteText.booking.info.split('(link)');
                return [
                  parts[0],
                  <a
                    key="agent-link"
                    href={siteText.booking.agentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1877f3', textDecoration: 'underline', fontWeight: 500, fontSize: '1.07rem' }}
                  >
                    (link)
                  </a>,
                  parts[1]
                ];
              })()}
            </p>
            <h2>Booking Form</h2>
            <form onSubmit={handleSubmit} style={{ maxWidth: 500 }}>
              <label>Name:<br />
                <input type="text" name="name" value={form.name} onChange={handleChange} required />
              </label><br /><br />
              <label>Email:<br />
                <input type="email" name="email" value={form.email} onChange={handleChange} required />
              </label><br /><br />
              <label>Arrival Date:<br />
                <DatePicker
                  selected={form.start}
                  onChange={date => setForm(prev => ({ ...prev, start: date, end: null }))}
                  filterDate={filterArrival}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select arrival (Friday)"
                  required
                />
              </label><br /><br />
              <label>Departure Date:<br />
                <DatePicker
                  selected={form.end}
                  onChange={date => setForm(prev => ({ ...prev, end: date }))}
                  filterDate={filterDeparture}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select departure (7 or 14 nights)"
                  required
                  minDate={getValidDepartures()[0]}
                  maxDate={getValidDepartures()[1]}
                  disabled={false}
                />
              </label><br /><br />
              <label>Number of Guests:<br />
                <input type="number" name="guests" min="1" max="5" value={form.guests} onChange={handleChange} required />
              </label><br /><br />
              <label>Number of Pets (up to 2):<br />
                <input type="number" name="pets" min="0" max="2" value={form.pets} onChange={handleChange} required />
              </label>
              {form.pets > 2 && (
                <div style={{ color: '#b91c1c', fontSize: '0.97rem' }}>Maximum 2 pets per booking.</div>
              )}
              <div style={{ fontSize: '0.97rem', color: '#555', marginTop: 2, marginBottom: 8 }}>Maximum 2 pets per booking.</div>
              <br />
              <label>Message:<br />
                <textarea name="message" value={form.message} onChange={handleChange} rows="3" />
              </label><br /><br />

              {/* --- Pricing summary --- */}
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e0e7ef',
                borderRadius: 7,
                padding: '1rem 1.2rem',
                margin: '0 0 1.3rem 0',
                color: '#12375e',
                fontSize: '1.1rem',
                fontWeight: 500
              }}>
                {loadingPricing ? (
                  <span>Loading pricing...</span>
                ) : pricingError ? (
                  <span style={{ color: '#b91c1c' }}>{pricingError}</span>
                ) : form.start && form.end ? (
                  (() => {
                    const priceInfo = calculateTotalPrice();
                    if (!priceInfo) return <span>Select valid dates for a price quote.</span>;
                    return <>
                      <div><strong>Pricing</strong></div>
                      <div>{priceInfo.weeks} week{priceInfo.weeks > 1 ? 's' : ''} @ £{priceInfo.weeklyPrice} per week</div>
                      {priceInfo.petFee > 0 && (
                        <div>Pet fee: £{priceInfo.petFee} (£20 per dog per stay)</div>
                      )}
                      <div style={{ marginTop: 6, fontWeight: 700 }}>Total: £{priceInfo.total}</div>
                      <div style={{ color: '#1a7f37', marginTop: 2 }}>Our best price, booking direct with us.</div>
                    </>;
                  })()
                ) : (
                  <span>Select arrival and departure dates to see your price.</span>
                )}
              </div>

              <button type="submit" style={{ marginTop: 18, background: '#12375e', color: '#fff', padding: '0.7rem 1.7rem', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '1.07rem' }}>Submit Booking Enquiry</button>
            </form>
            <div style={{ marginTop: 18, color: '#0f2339', fontSize: '1.01rem', fontStyle: 'italic' }}>
              We aim to respond to all booking enquiries within 24 hours, please email figtree@dhowell.net directly if you have any issues.
            </div>
            {submitStatus === "success" && (
              <div style={{ marginTop: 18, color: '#207c2d', fontWeight: 600 }}>
                Thank you for your enquiry! We'll be in touch soon.
              </div>
            )}
            {submitStatus === "error" && (
              <div style={{ marginTop: 18, color: '#b91c1c', fontWeight: 600 }}>
                Sorry, there was a problem submitting your enquiry. Please try again later or email figtree@dhowell.net directly..
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Booking;
